import constants from 'constants/constants';
import { Controller, useForm } from 'react-hook-form';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationRenameProject } from 'api/mutations/renameProject.mutation';

import classes from 'components/common/ProgramLibraryDropdownMenu/components/RenameProjectModal/RenameProjectModal.module.scss';
import { CustomizedFormErrorMessage } from 'components/common/index';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

interface Props {
    showModal: boolean;
    onHideModal: () => void;
}

interface IProjectName {
    projectName: string;
}

export const RenameProjectModal = ({ showModal, onHideModal }: Props) => {
    const { project, update } = useProjectTreeStore();

    const { handleSnackBar } = useCustomSnackBar();
    const { mutate, isLoading } = useMutationRenameProject();

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        reset,
    } = useForm<IProjectName>({
        values: {
            projectName: project ? project?.title : '',
        },
    });

    const handleSave = (value: IProjectName) => {
        if (!project) {
            return;
        }
        mutate(
            {
                projectName: value.projectName,
                projectId: project.id,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Successfully renamed!', 'success');
                    update({ project: { ...project, title: value.projectName } });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                },
            },
        );
    };

    const closeModal = () => {
        onHideModal();
        reset();
    };

    return (
        <Modal
            open={showModal}
            onClose={closeModal}
            title={'Project Rename'}
            subtitle={<>Current name: {project?.title}</>}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoading}
                        disabled={isLoading}
                        onClick={handleSubmit(handleSave)}
                    >
                        Rename
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit(handleSave)} autoComplete={'off'}>
                <div className={classes.content}>
                    <div className={classes.inputWrapper}>
                        <label>New name</label>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    id={'projectName'}
                                    className={classes.input}
                                    placeholder={'Enter project name...'}
                                    maxLength={constants.MAX_PROJECT_NAME_LENGTH}
                                    onClear={() => {
                                        setValue('projectName', '');
                                    }}
                                />
                            )}
                            name={'projectName'}
                            control={control}
                            rules={{
                                validate: {
                                    min: (value) => (value && value.length >= 2) || 'must be at least 2 characters',
                                },
                            }}
                        />
                        {errors.projectName && <CustomizedFormErrorMessage text={errors.projectName.message} />}
                    </div>
                </div>
            </form>
        </Modal>
    );
};
